import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet';

import { useMediaQuery } from '@mui/material';
import { useTheme, styled } from '@classify/react-core-components/styles';

import Typography, { Props as TypographyProps } from '@classify/react-core-components/Typography';
import Button from '@classify/react-core-components/Button';
import Stack from '@classify/react-core-components/Stack';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';

import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { ImageCdn } from '@openclassrooms/images-cdn';
import { getPageTitle } from '@openclassrooms/utils/page-header';
import { Translate } from '@openclassrooms/translations';
import { getPathName, ROUTES } from '@openclassrooms/routing';

export interface Props {
  titleLabel: string;
  detailsLabel: string;
  pictureUrl: string;
  pictureWidth: number;
  hasButton?: boolean;
  httpStatus: number;
  customPrimaryButton?: ReactNode;
  content?: ReactNode;
  actionLink?: string;
  children?: ReactNode;
}

const Wrapper = styled(Stack)(({ theme: { containerQueries, spacing } }) => ({
  flexDirection: 'column-reverse',
  gap: spacing(2),
  [containerQueries.up('md')]: {
    flexDirection: 'row',
    gap: spacing(0),
  },
}));

export const ParagraphError = ({
  children,
  ...props
}: {
  children: string;
  props?: TypographyProps;
}) => (
  <Typography component="p" variant="bodySmall" {...props}>
    {children}
  </Typography>
);

export const TitleError = ({ children }: { children: string }) => (
  <Typography component="h2" variant="h5" sx={{ mt: 2 }}>
    {children}
  </Typography>
);

const ErrorWrapper = ({
  pictureUrl,
  titleLabel,
  detailsLabel,
  pictureWidth,
  hasButton = false,
  httpStatus,
  customPrimaryButton = null,
  content = null,
  actionLink,
  children,
}: Props) => {
  const { isConnected } = useCurrentUserContext();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const illustrationWidth = isMobile ? '100%' : pictureWidth;

  return (
    <ContentWrapper
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '90vh',
        containerType: 'inline-size',
      }}
    >
      <Helmet>
        <title>{getPageTitle(titleLabel)}</title>
        <meta name="prerender-status-code" content={String(httpStatus)} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Wrapper spacing={4} alignItems="center" justifyContent="center">
        <Stack gap={1} sx={{ width: { md: '60%' }, mb: 4 }}>
          <Typography component="h1" variant="h2" data-testid={`error-${httpStatus}`}>
            {titleLabel}
          </Typography>
          {children ? (
            <div>{children}</div>
          ) : (
            <Typography component="p" variant="subtitle1">
              {/* eslint-disable-next-line react/no-danger */}
              <span dangerouslySetInnerHTML={{ __html: detailsLabel }} />
              {content}
            </Typography>
          )}
          {(customPrimaryButton || hasButton) && (
            <Stack flexDirection="row" gap={2} sx={{ mt: 4 }}>
              {customPrimaryButton}
              {hasButton && (
                <Button
                  variant={customPrimaryButton ? 'secondary' : 'primary'}
                  component="a"
                  href={
                    actionLink ??
                    (isConnected
                      ? getPathName(ROUTES.DASHBOARD.INDEX)
                      : getPathName(ROUTES.HOMEPAGE.INDEX))
                  }
                >
                  <Translate content="errors.button_label" />
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        <ImageCdn alt="" src={pictureUrl} width={illustrationWidth} />
      </Wrapper>
    </ContentWrapper>
  );
};

export default ErrorWrapper;
